import React, { useEffect, useCallback } from "react";
import "./App.css"
import AppRoutes from "./Components/AppRoutes/AppRoutes";
import LoadCSSforfirstTime from "./CloudStorage/LoadCSSforfirstTime";
import HelmetAllInOne from "./Components/Seo/HelmetAllInOne";
import "react-toastify/dist/ReactToastify.css";
import { RouteBaseStateProvider } from "./Context/RouteBaseStateContext";
import { AllFilterBaseProvider } from "./Context/AllFilterBaseContext";
import { PaginationProvider } from "./Context/ChatPaginationContext";

export default function App() {
  const path = "Assets/scss/app.module.scss";

  // Global keyboard navigation handler for li, a, button elements
  const handleKeyboardNavigation = useCallback((e) => {
    // Get the currently focused element
    const activeElement = document.activeElement;
    
    // Check if focused element is li, a without href, or button
    const isInteractiveElement = 
      activeElement.tagName === 'LI' ||
      (activeElement.tagName === 'A' && !activeElement.getAttribute('href')) ||
      activeElement.tagName === 'BUTTON' ||
      activeElement.getAttribute('role') === 'button' ||
      activeElement.getAttribute('tabindex') === '0';
    
    // Don't interfere if user is typing in form elements
    const isTypingElement = 
      activeElement.tagName === 'INPUT' || 
      activeElement.tagName === 'TEXTAREA' || 
      activeElement.tagName === 'SELECT' ||
      activeElement.isContentEditable;
    
    if (isTypingElement) return;

    // Handle Enter/Return key for interactive elements
    if ((e.key === 'Enter' || e.key === ' ') && isInteractiveElement) {
      e.preventDefault();
      
      // Handle different element types
      switch (activeElement.tagName) {
        case 'LI':
          // Trigger click on first interactive child or the li itself
          const firstInteractive = activeElement.querySelector('a, button, [role="button"], [tabindex="0"]');
          if (firstInteractive) {
            firstInteractive.click();
          } else {
            activeElement.click();
          }
          break;
          
        case 'A':
        case 'BUTTON':
          activeElement.click();
          break;
          
        default:
          // For elements with role="button" or tabindex="0"
          if (activeElement.getAttribute('role') === 'button' || 
              activeElement.getAttribute('tabindex') === '0') {
            activeElement.click();
          }
          break;
      }
    }
  }, []);

  // Global keyboard shortcuts handler (existing functionality)
  const handleGlobalKeyDown = useCallback((e) => {
    // Don't trigger shortcuts if user is typing in an input, textarea, or contenteditable
    const isTypingElement = 
      e.target.tagName === 'INPUT' || 
      e.target.tagName === 'TEXTAREA' || 
      e.target.tagName === 'SELECT' ||
      e.target.isContentEditable;
    
    if (isTypingElement) return;

    // First handle the keyboard navigation for interactive elements
    handleKeyboardNavigation(e);

    // Then handle other global shortcuts
    switch (e.key) {
      case '/':
        // Search focus shortcut
        e.preventDefault();
        const searchInput = document.querySelector('input[type="search"], input[placeholder*="Search"], .search-input');
        if (searchInput) {
          searchInput.focus();
        }
        break;

      case '?':
        // Show keyboard shortcuts help
        e.preventDefault();
        console.log('Show keyboard shortcuts help');
        break;

      case 'Escape':
        // Close modals, sidebars, dropdowns
        const activeModal = document.querySelector('.modal.show, .popupmodal.show');
        const activeSidebar = document.querySelector('.dashboard-sidebar.show, .sidebar.show');
        const activeDropdown = document.querySelector('.dropdown.show, .menu.open');
        
        if (activeModal) {
          const closeButton = activeModal.querySelector('[data-dismiss="modal"], .close-button, .btn-close');
          if (closeButton) closeButton.click();
        } else if (activeSidebar) {
          const sidebarCloseEvent = new CustomEvent('closeMobileSidebar');
          document.dispatchEvent(sidebarCloseEvent);
        } else if (activeDropdown) {
          const dropdownToggle = activeDropdown.querySelector('.dropdown-toggle, .menu-toggle');
          if (dropdownToggle) dropdownToggle.click();
        }
        break;

      // Custom shortcuts for your app
      case 'd':
        if (e.ctrlKey || e.metaKey) {
          // Ctrl/Cmd + D - Go to dashboard
          e.preventDefault();
          const currentLang = localStorage.getItem("laung") || "en";
          window.location.href = `/${currentLang}/dashboard`;
        }
        break;

      case 'h':
        if (e.ctrlKey || e.metaKey) {
          // Ctrl/Cmd + H - Go to home
          e.preventDefault();
          const currentLang = localStorage.getItem("laung") || "en";
          window.location.href = `/${currentLang}`;
        }
        break;

      case 'p':
        if (e.ctrlKey || e.metaKey) {
          // Ctrl/Cmd + P - Go to profile
          e.preventDefault();
          const currentLang = localStorage.getItem("laung") || "en";
          window.location.href = `/${currentLang}/dashboard/manageprofile`;
        }
        break;

      default:
        break;
    }
  }, [handleKeyboardNavigation]);

  // Add tabindex to all interactive elements for better keyboard navigation
  const setupKeyboardNavigation = useCallback(() => {
    // Find all li, a (without href), and button elements that should be interactive
    const interactiveElements = document.querySelectorAll(`
      li[onclick],
      li[role="button"],
      li:has(> a:only-child):not(:has(button, input, textarea, select)),
      a:not([href]):not([tabindex]),
      button:not([tabindex]),
      [role="button"]:not([tabindex])
    `);

    interactiveElements.forEach((element, index) => {
      // Add tabindex if not already present
      if (!element.hasAttribute('tabindex')) {
        element.setAttribute('tabindex', '0');
      }
      
      // Add keyboard event listener for Enter/Space
      const handleKeyPress = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          element.click();
        }
      };
      
      // Store the handler on the element for cleanup
      element._keyboardHandler = handleKeyPress;
      element.addEventListener('keydown', handleKeyPress);
      
      // Add visual indicator for keyboard focus
      element.addEventListener('focus', () => {
        element.classList.add('keyboard-focused');
      });
      
      element.addEventListener('blur', () => {
        element.classList.remove('keyboard-focused');
      });
    });
    
    return () => {
      // Cleanup function
      interactiveElements.forEach(element => {
        if (element._keyboardHandler) {
          element.removeEventListener('keydown', element._keyboardHandler);
          delete element._keyboardHandler;
        }
      });
    };
  }, []);

  useEffect(() => {
    LoadCSSforfirstTime(path);
    
    // Add global keyboard event listener
    window.addEventListener('keydown', handleGlobalKeyDown);
    
    // Setup initial keyboard navigation
    const cleanup = setupKeyboardNavigation();
    
    // Setup observer to handle dynamically added elements
    const observer = new MutationObserver(() => {
      setupKeyboardNavigation();
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
      if (cleanup) cleanup();
      observer.disconnect();
    };
  }, [path, handleGlobalKeyDown, setupKeyboardNavigation]);

  return (
    <div className="keyboard-navigation-enabled">
      <RouteBaseStateProvider>
        <AllFilterBaseProvider>
          <HelmetAllInOne />
          <PaginationProvider>
            <AppRoutes />
          </PaginationProvider>
        </AllFilterBaseProvider>
      </RouteBaseStateProvider>
    </div>
  );
}